import { useMemo, useState } from 'react';
import { useGetPickupCyclesQuery } from '@/pages/Business/pages/PickupCycles/services';
import {
  PickupCyclesFilter,
  WeekDayKey,
} from '@/pages/Business/pages/PickupCycles/models';

const EMPTY_FILTER: PickupCyclesFilter = {
  commerceId: '',
  branchId: '',
  tag: '',
  cycleTime: '',
  days: [],
};

export const usePickupCycles = () => {
  const [filter, setFilter] = useState<PickupCyclesFilter>(EMPTY_FILTER);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 100 });

  const { data, isLoading, isFetching } = useGetPickupCyclesQuery({
    filters: filter,
    paginated: pagination,
  });

  const cycles = useMemo(() => data?.items ?? [], [data]);
  const totalItems = data?.count ?? 0;
  const totalPages = useMemo(
    () => Math.ceil(totalItems / pagination.pageSize),
    [totalItems, pagination.pageSize],
  );

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPagination({ page: 1, pageSize });
  };

  // Cualquier cambio de filtro vuelve a la primera página.
  const updateFilter = (patch: Partial<PickupCyclesFilter>) => {
    setFilter((prev) => ({ ...prev, ...patch }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleCommerceFilterChange = (commerceId: string) => {
    // Al cambiar de comercio, la sucursal seleccionada deja de tener sentido.
    updateFilter({ commerceId, branchId: '' });
  };

  const handleBranchFilterChange = (branchId: string) => {
    updateFilter({ branchId });
  };

  const handleTagFilterChange = (tag: string) => {
    updateFilter({ tag });
  };

  const handleCycleTimeFilterChange = (cycleTime: string) => {
    updateFilter({ cycleTime });
  };

  const handleDaysFilterChange = (days: WeekDayKey[]) => {
    updateFilter({ days });
  };

  const clearFilter = () => {
    setFilter(EMPTY_FILTER);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = Boolean(
    filter.commerceId ||
      filter.branchId ||
      filter.tag ||
      filter.cycleTime ||
      (filter.days && filter.days.length > 0),
  );

  return {
    cycles,
    isLoading: isLoading || isFetching,
    totalItems,
    totalPages,
    currentPage: pagination.page,
    pageSize: pagination.pageSize,
    commerceFilter: filter.commerceId ?? '',
    branchFilter: filter.branchId ?? '',
    tagFilter: filter.tag ?? '',
    cycleTimeFilter: filter.cycleTime ?? '',
    daysFilter: filter.days ?? [],
    hasActiveFilters,
    handlePageChange,
    handlePageSizeChange,
    handleCommerceFilterChange,
    handleBranchFilterChange,
    handleTagFilterChange,
    handleCycleTimeFilterChange,
    handleDaysFilterChange,
    clearFilter,
  };
};
